import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import useDocumentTypes, { labelForDocumentType } from '../../hooks/useDocumentTypes';

const TABS = [
  { id: 'contacts', label: 'Contacts', url: '/admin/data-deleted/contacts', queuePath: '/admin/all-statuses/contact-deletion' },
  { id: 'accounts', label: 'Accounts', url: '/admin/data-deleted/accounts', queuePath: '/admin/all-statuses/account-deletion' },
  { id: 'jobs', label: 'Job requirements', url: '/admin/data-deleted/job-requirements', queuePath: null },
  { id: 'documents', label: 'Documents', url: '/admin/data-deleted/documents', queuePath: null }
];

function statusLabel(row) {
  if (row.status === 'deleted' || row.is_marked_for_deletion) return 'Deleted';
  return row.status || '—';
}

export default function AdminDeletedData() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const { documentTypes } = useDocumentTypes();

  const load = useCallback(async () => {
    setLoading(true);
    const cfg = TABS[tab];
    try {
      const r = await api.get(cfg.url, { params: { page: 1, limit: 200 } });
      const d = r.data?.data;
      setItems(d?.items || []);
      setTotal(d?.total ?? 0);
    } catch (e) {
      toast.error(e.response?.data?.message || `Failed to load deleted ${cfg.label.toLowerCase()}`);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const cfg = TABS[tab];

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Deleted Data
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Records in <strong>Deleted</strong> state stay in the database until an administrator <strong>Archives</strong> them (deletion
        queue where available). Only <strong>archived</strong> rows are included in backup zips; permanent removal happens after backup,
        from Archived Data.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Contacts and accounts can be archived from their deletion queues. Job requirements and documents: use{' '}
        <strong>Data Administration → Archived Data</strong> to archive, export backup, then permanently delete when appropriate.
      </Alert>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {TABS.map((t, i) => (
          <Tab key={t.id} label={t.label} value={i} />
        ))}
      </Tabs>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Deleted {cfg.label.toLowerCase()} ({total})
            </Typography>
            {cfg.queuePath && (
              <Button size="small" variant="outlined" onClick={() => navigate(cfg.queuePath)}>
                Open deletion queue
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {loading ? (
        <CircularProgress />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {cfg.id === 'contacts' && (
                <>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Display status</TableCell>
                  <TableCell>DB status</TableCell>
                  <TableCell>Marked purge</TableCell>
                  <TableCell>Scheduled purge</TableCell>
                </>
              )}
              {cfg.id === 'accounts' && (
                <>
                  <TableCell>Company</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Display status</TableCell>
                  <TableCell>DB status</TableCell>
                  <TableCell>Marked purge</TableCell>
                  <TableCell>Scheduled purge</TableCell>
                </>
              )}
              {cfg.id === 'jobs' && (
                <>
                  <TableCell>Title</TableCell>
                  <TableCell>Job code</TableCell>
                  <TableCell>Display status</TableCell>
                  <TableCell>DB status</TableCell>
                  <TableCell>Marked purge</TableCell>
                  <TableCell>Scheduled purge</TableCell>
                </>
              )}
              {cfg.id === 'documents' && (
                <>
                  <TableCell>Title</TableCell>
                  <TableCell>File</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Marked purge</TableCell>
                  <TableCell>Scheduled purge</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary">Nothing in deleted bucket for this type.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((row) => (
                <TableRow key={row.id} hover>
                  {cfg.id === 'contacts' && (
                    <>
                      <TableCell>
                        {row.first_name} {row.last_name}
                      </TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>
                        <Chip label={statusLabel(row)} size="small" color="warning" variant="outlined" />
                      </TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{row.is_marked_for_deletion ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        {row.deletion_scheduled_for ? new Date(row.deletion_scheduled_for).toLocaleString() : '—'}
                      </TableCell>
                    </>
                  )}
                  {cfg.id === 'accounts' && (
                    <>
                      <TableCell>{row.company_name}</TableCell>
                      <TableCell>{row.account_code}</TableCell>
                      <TableCell>
                        <Chip label={statusLabel(row)} size="small" color="warning" variant="outlined" />
                      </TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{row.is_marked_for_deletion ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        {row.deletion_scheduled_for ? new Date(row.deletion_scheduled_for).toLocaleString() : '—'}
                      </TableCell>
                    </>
                  )}
                  {cfg.id === 'jobs' && (
                    <>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>{row.job_code}</TableCell>
                      <TableCell>
                        <Chip label={statusLabel(row)} size="small" color="warning" variant="outlined" />
                      </TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{row.is_marked_for_deletion ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        {row.deletion_scheduled_for ? new Date(row.deletion_scheduled_for).toLocaleString() : '—'}
                      </TableCell>
                    </>
                  )}
                  {cfg.id === 'documents' && (
                    <>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>{row.file_name}</TableCell>
                      <TableCell>{labelForDocumentType(row.document_type, documentTypes) || row.document_type || '—'}</TableCell>
                      <TableCell>{row.is_marked_for_deletion ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        {row.deletion_scheduled_for ? new Date(row.deletion_scheduled_for).toLocaleString() : '—'}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
